import { useEffect, useState, useCallback } from "react";
import { Webhook, Loader2, RefreshCw, CheckCircle2, XCircle, ShieldCheck, ShieldAlert, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 text-right break-all">{children}</span>
  </div>
);

const Pill = ({ ok, yes = "Active", no = "Not set" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {ok ? yes : no}
  </span>
);

export default function AdminSquareWebhook() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/square/webhook");
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load webhook status");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/square/webhook/provision");
      toast.success(`Webhook ${r.data.status} — real-time payments are now wired`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Provisioning failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  const s = status || {};
  const healthy = s.exists_in_square && s.enabled && s.signature_key_configured;

  return (
    <div data-testid="admin-square-webhook" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-xl bg-blue-600/10 flex items-center justify-center"><Webhook className="h-5 w-5 text-blue-600" /></div>
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900">Square Payments Webhook</h1>
          <p className="text-sm text-slate-500">Real-time payment &amp; refund events that auto-confirm bookings the moment a customer pays.</p>
        </div>
      </div>

      <div className={`mt-5 rounded-2xl border p-4 flex items-start gap-3 ${healthy ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}>
        {healthy ? <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />}
        <div className="text-sm">
          <p className="font-semibold text-slate-800">{healthy ? "Live & verified" : "Action needed"}</p>
          <p className="text-slate-600 mt-0.5">
            {healthy
              ? "Square is sending signed events to OperaMind. Paid bookings update to Paid automatically."
              : s.exists_in_square
                ? "The subscription exists but signature verification isn't enabled yet. Click Provision to store the signing key so we can verify Square's requests."
                : "No webhook subscription is registered in Square yet, so payments won't update bookings. Click Provision to set it up automatically."}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <Row label="Endpoint URL">
          <button data-testid="copy-webhook-url" onClick={() => copy(s.notification_url)} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-blue-600">
            {s.notification_url} <Copy className="h-3.5 w-3.5" />
          </button>
        </Row>
        <Row label="Subscribed events">{(s.events || []).join(", ")}</Row>
        <Row label="App access token"><Pill ok={s.app_token_configured} yes="Configured" no="Missing" /></Row>
        <Row label="Subscription in Square"><Pill ok={s.exists_in_square} yes={s.enabled ? "Enabled" : "Disabled"} no="Not found" /></Row>
        <Row label="Signature verification">
          <span className="inline-flex items-center gap-2">
            <Pill ok={s.signature_key_configured} yes="Enabled" no="Disabled" />
            {s.signature_key_source && <span className="text-xs text-slate-400">({s.signature_key_source})</span>}
          </span>
        </Row>
        {s.subscription_id && <Row label="Subscription ID"><code className="text-xs">{s.subscription_id}</code></Row>}
        {s.error && <Row label="Square API"><span className="text-rose-600 text-xs">{s.error}</span></Row>}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button data-testid="provision-webhook-btn" onClick={provision} disabled={busy || !s.app_token_configured} className="bg-blue-600 hover:bg-blue-700 gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />}
          {s.exists_in_square ? "Re-provision & refresh key" : "Provision webhook"}
        </Button>
        <Button data-testid="refresh-webhook-btn" variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>
      {!s.app_token_configured && <p className="mt-2 text-xs text-rose-600">SQUARE_ACCESS_TOKEN is not configured on the server — add the application access token before provisioning.</p>}
    </div>
  );
}
